import { DiceError, DiceExpression, type Resolver, type RollResult } from '../dice/dice.ts';
import type { Random } from '../dice/random.ts';
import { javaEqualsIgnoreCase, javaIsBlank, javaParseLong, javaStrip } from '../javaText.ts';
import { FIELD_TYPES, allFields, fieldFor, isRollable, type SheetField, type SheetTemplate } from './sheetModel.ts';

/**
 * Reads a character sheet the way its template says to, as the desktop's
 * `sheets.SheetEvaluator`: each field's value (or the template's default), derived fields
 * from their formulas, resources' maximums, and rolls with every `@field` filled in. Cheap
 * enough to rebuild on every keystroke, which is how a sheet keeps its derived values live.
 *
 * A derived field whose formula can't be worked out (a typo, a loop, a text field used as a
 * number) doesn't stop the rest: it has no value, and {@link problem} says why - in the
 * desktop's words, which depend on what was asked first, as there.
 */
export class SheetEvaluator {
  private readonly fields = new Map<string, SheetField>();
  private readonly cache = new Map<string, bigint>();
  private readonly problems = new Map<string, string>();
  private readonly evaluating = new Set<string>();
  private readonly values: ReadonlyMap<string, string>;

  constructor(
    readonly template: SheetTemplate,
    values: ReadonlyMap<string, string> | null,
  ) {
    this.values = values ?? new Map();
    for (const field of allFields(template)) if (!this.fields.has(field.key)) this.fields.set(field.key, field);
  }

  /** What's written in the field, or its default; for a resource, its current value's text. */
  raw(key: string): string {
    const value = this.values.get(key);
    if (value !== undefined) return value;
    const field = this.fields.get(key);
    if (!field) return '';
    if (field.type === 'RESOURCE') {
      if (startsBelowMax(field)) return field.defaultValue;
      const max = this.max(key);
      return max === null ? '' : String(max);
    }
    return field.defaultValue;
  }

  isChecked(key: string): boolean {
    return truthy(this.raw(key));
  }

  /** The chosen option's label (the default, or the first option, when none is chosen). */
  choiceLabel(key: string): string {
    const field = this.fields.get(key);
    if (!field || field.options.length === 0) return this.raw(key);
    return optionLabel(field.options[this.selectedIndex(field)]!);
  }

  /** A numeric field's value; null for text fields, and for a derived field that can't be worked out. */
  number(key: string): bigint | null {
    try {
      return this.resolve(key);
    } catch (e) {
      if (!(e instanceof DiceError)) throw e;
      if (!this.problems.has(key)) this.problems.set(key, e.message);
      return null;
    }
  }

  /** A resource's maximum: its formula's value, or the number entered, or its default. */
  max(key: string): bigint | null {
    const field = this.fields.get(key);
    if (!field || field.type !== 'RESOURCE') return null;
    try {
      return this.resolveMax(field);
    } catch (e) {
      if (!(e instanceof DiceError)) throw e;
      if (!this.problems.has(key)) this.problems.set(key, e.message);
      return null;
    }
  }

  /** Why a field has no value, if it hasn't. */
  problem(key: string): string | null {
    const field = this.fields.get(key);
    if (field && (field.type === 'DERIVED' || field.type === 'NUMBER' || field.type === 'RESOURCE')) this.number(key);
    return this.problems.get(key) ?? null;
  }

  /** Resolves `@key` and `@key_max` in formulas and rolls. */
  resolver(): Resolver {
    return (key) => this.resolve(key);
  }

  /** A field's 🎲: its roll, or - for a dice field - what's written in it. */
  roll(key: string, random: Random): RollResult {
    const field = this.fields.get(key);
    if (!field || !isRollable(field)) throw new DiceError(`"${key}" has nothing to roll.`);
    const expression = field.type === 'DICE' ? this.raw(key) : field.roll;
    if (javaIsBlank(expression)) throw new DiceError(`${field.label} has no roll written in yet.`);
    return this.rollExpression(expression, random);
  }

  rollExpression(expression: string, random: Random): RollResult {
    return DiceExpression.parse(expression).roll(random, this.resolver());
  }

  /** Initiative as the template's Initiative field says: its roll, or its value as a fixed number; null if none. */
  rollInitiative(random: Random): RollResult | null {
    const field = fieldFor(this.template, 'INITIATIVE');
    if (!field) return null;
    return isRollable(field) ? this.roll(field.key, random) : this.rollExpression(`@${field.key}`, random);
  }

  /** The Hit points resource's current and maximum values, if the template has one. */
  hitPoints(): [bigint, bigint] | null {
    const field = fieldFor(this.template, 'HIT_POINTS');
    if (!field || field.type !== 'RESOURCE') return null;
    const current = this.number(field.key);
    const maximum = this.max(field.key);
    return current === null || maximum === null ? null : [current, maximum];
  }

  /** One line for places that show a sheet without opening it: "Hit Points 24/30 · Armor Class 16 · Initiative +3". */
  summary(): string {
    const parts: string[] = [];
    const hp = this.hitPoints();
    if (hp) parts.push(`${fieldFor(this.template, 'HIT_POINTS')?.label ?? 'HP'} ${hp[0]}/${hp[1]}`);
    const defense = fieldFor(this.template, 'DEFENSE');
    if (defense) {
      const value = this.number(defense.key);
      if (value !== null) parts.push(`${defense.label} ${value}`);
    }
    const initiative = fieldFor(this.template, 'INITIATIVE');
    if (initiative) {
      const value = this.number(initiative.key);
      // A bonus added to a roll reads "+2"; a plain order (percentile Dexterity) reads "40".
      if (value !== null) parts.push(`${initiative.label} ${isRollable(initiative) && value >= 0n ? '+' : ''}${value}`);
    }
    return parts.join(' · ');
  }

  // --- resolution ---

  private resolve(key: string): bigint | null {
    const field = this.fields.get(key);
    if (!field && key.endsWith('_max')) {
      const base = this.fields.get(key.slice(0, -4));
      if (base && base.type === 'RESOURCE') return this.resolveMax(base);
    }
    if (!field) return null;
    switch (field.type) {
      case 'NUMBER':
        return parseNumber(field, this.raw(key));
      case 'CHECKBOX':
        return truthy(this.raw(key)) ? 1n : 0n;
      case 'CHOICE':
        return this.choiceValue(field);
      case 'RESOURCE': {
        const current = this.values.get(key);
        if (current === undefined || javaIsBlank(current)) return startsBelowMax(field) ? parseNumber(field, field.defaultValue) : this.resolveMax(field);
        return parseNumber(field, current);
      }
      case 'DERIVED':
        return this.derived(field);
      default:
        throw new DiceError(`@${key} is a ${FIELD_TYPES[field.type].toLowerCase()} field, not a number.`);
    }
  }

  private derived(field: SheetField): bigint {
    const cached = this.cache.get(field.key);
    if (cached !== undefined) return cached;
    const problem = this.problems.get(field.key);
    if (problem !== undefined) throw new DiceError(problem);
    if (this.evaluating.has(field.key)) {
      const loop = [...this.evaluating];
      const path = `${loop
        .slice(loop.indexOf(field.key))
        .map((k) => `@${k}`)
        .join(' → ')} → @${field.key}`;
      throw new DiceError(`These fields depend on each other in a loop: ${path}.`);
    }
    if (field.formula === '') throw new DiceError(`${field.label} has no formula.`);
    this.evaluating.add(field.key);
    try {
      const value = DiceExpression.parse(field.formula).evaluate(this.resolver());
      this.cache.set(field.key, value);
      return value;
    } catch (e) {
      if (e instanceof DiceError) this.problems.set(field.key, e.message);
      throw e;
    } finally {
      this.evaluating.delete(field.key);
    }
  }

  private resolveMax(field: SheetField): bigint {
    if (field.formula !== '') {
      const cacheKey = `${field.key}.max`;
      const cached = this.cache.get(cacheKey);
      if (cached !== undefined) return cached;
      if (this.evaluating.has(cacheKey)) throw new DiceError(`@${field.key}_max depends on itself.`);
      this.evaluating.add(cacheKey);
      try {
        const value = DiceExpression.parse(field.formula).evaluate(this.resolver());
        this.cache.set(cacheKey, value);
        return value;
      } finally {
        this.evaluating.delete(cacheKey);
      }
    }
    const entered = this.values.get(`${field.key}.max`);
    return parseNumber(field, entered !== undefined && !javaIsBlank(entered) ? entered : field.defaultValue);
  }

  private choiceValue(field: SheetField): bigint {
    if (field.options.length === 0) return 0n;
    const index = this.selectedIndex(field);
    return optionValue(field.options[index]!, index);
  }

  private selectedIndex(field: SheetField): number {
    const selected = javaStrip(this.values.get(field.key) ?? field.defaultValue);
    const index = field.options.findIndex((option) => javaEqualsIgnoreCase(optionLabel(option), selected));
    return Math.max(0, index);
  }
}

/** A resource with a formula maximum and a default starts at that default (stress at 0 of 9). */
const startsBelowMax = (field: SheetField) => field.formula !== '' && !javaIsBlank(field.defaultValue);

function parseNumber(field: SheetField, text: string | null): bigint {
  const trimmed = javaStrip(text ?? '').replaceAll('+', '');
  if (trimmed === '') return 0n;
  const value = javaParseLong(trimmed);
  if (value === null) throw new DiceError(`${field.label} should be a whole number, not "${javaStrip(text ?? '')}".`);
  return value;
}

/** "true", "1", "yes" or "x". */
export function truthy(text: string | null): boolean {
  const value = javaStrip(text ?? '').toLowerCase();
  return value === 'true' || value === '1' || value === 'yes' || value === 'x';
}

/** "Trained=2" -> "Trained". */
export function optionLabel(option: string): string {
  const equals = option.lastIndexOf('=');
  return equals > 0 ? javaStrip(option.slice(0, equals)) : javaStrip(option);
}

/** "Trained=2" -> 2; a plain option is worth its position. */
export function optionValue(option: string, index: number): bigint {
  const equals = option.lastIndexOf('=');
  if (equals > 0) return javaParseLong(javaStrip(option.slice(equals + 1)).replaceAll('+', '')) ?? BigInt(index);
  return BigInt(index);
}
